import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Search,
  Calendar,
  ChevronDown,
  Clapperboard,
  Film,
  FileText,
  Globe,
  GraduationCap,
  ShieldCheck,
  BookOpen,
  // Award,
  Megaphone,
  Star,
  Hash,
  Mail,
  ListChecks,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import logo from '../assets/primary-logo.png'
import { useEvents } from '@/features/events/queries'
interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  badge?: number
  end?: boolean
}

interface NavSection {
  section: string
  items: NavItem[]
}

const NAVIGATION: NavSection[] = [
  {
    section: 'OVERVIEW',
    items: [{ to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true }],
  },
  {
    section: 'CONTENT',
    items: [
      { to: '/events', label: 'Events', icon: Calendar, badge: 20 },
      { to: '/watch', label: 'Watch', icon: Film },
      // { to: '/watch/analytics', label: 'Watch Analytics', icon: BarChart3 },
    ],
  },
  // {
  //   section: 'COMMERCE',
  //   items: [
  //     { to: '/orders', label: 'Orders', icon: ShoppingBag, badge: 8 },
  //     { to: '/rewards-ledger', label: 'Rewards Ledger', icon: CreditCard },
  //     { to: '/tier-system', label: 'Tier System', icon: Shield },
  //   ],
  // },
  {
    section: "USERS",
    items: [
      { to: '/users', label: 'Users', icon: Users },
      { to: '/creator-applications', label: 'Creator', icon: Clapperboard },
      { to: '/playbook-signups', label: 'Playbook', icon: BookOpen },
      { to: '/waitlist-signups', label: 'Waitlist', icon: ListChecks },
      { to: '/influencer-applications', label: 'Influencer', icon: Star },
      { to: '/marketing/newsletter', label: 'Newsletter', icon: Mail },
    ],
  },
  {
    section: 'COMMUNITY',
    items: [
      { to: '/community/access', label: 'Access Control', icon: ShieldCheck },
      { to: '/community/communities', label: 'Communities', icon: Globe },
      { to: '/community/channels', label: 'Channels', icon: Hash },
      { to: '/community/posts', label: 'Posts', icon: FileText },
      { to: '/classroom', label: 'Classroom', icon: GraduationCap },
    ],
  },
  // {
  //   section: 'RGA',
  //   items: [
  //     { to: '/rga-applications', label: 'RGA Applications', icon: Award },
  //   ],
  // },
  {
    section: 'MARKETING',
    items: [
      { to: '/marketing/broadcast', label: 'Broadcast', icon: Megaphone }
    ],
  },
  // {
  //   section: 'SYSTEM',
  //   items: [
  //     { to: '/media-library', label: 'Media Library', icon: ImageIcon },
  //     { to: '/admin-team', label: 'Admin Team', icon: UserPlus },
  //     { to: '/settings', label: 'Settings', icon: Settings },
  //   ],
  // },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  /** TODO(auth): once login lands, replace these props with `useCurrentUser()`. */
  user?: { name: string; initials: string; role: string }
  onLogout: () => void
}

const FALLBACK_USER = { name: 'Admin', initials: 'A', role: 'Sign in' }

export default function Sidebar({ isOpen, onClose, user = FALLBACK_USER }: SidebarProps) {
  const { data } = useEvents({ tag: 'all' })
  const eventsCount = data?.counts?.all
  return (
    <aside
      id="primary-sidebar"
      aria-label="Primary navigation"
      className={`flex flex-col w-[248px] shrink-0 bg-secondary border-r border-border-dark/9
        fixed bottom-0 left-0 top-0 z-30 h-full lg:static lg:top-auto lg:bottom-auto lg:h-full
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      <div className="flex shrink-0 items-center border-b border-border-dark/9 px-5 py-3 h-[60px]">
        <img src={logo} alt="Rohan Gurram" className="hidden h-8 w-auto sm:block" />
      </div>

      <div className="shrink-0 p-4">
        <label className="flex cursor-text items-center gap-2 rounded-lg border border-border-dark/9 bg-warm-cream px-3 py-2 transition-colors focus-within:border-transparent">
          <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-muted" strokeWidth={2} />
          <input
            type="text"
            name="sidebar-quick-search"
            role="searchbox"
            aria-label="Quick search"
            autoComplete="off"
            placeholder="Quick search"
            className="sidebar-quick-search-input min-w-0 flex-1 border-0 bg-transparent p-0 text-xs-plus text-text-dark outline-none placeholder:text-text-secondary focus:outline-none focus-visible:outline-none focus:ring-0 focus:ring-offset-0"
          />
        </label>
      </div>

      <nav aria-label="Sections" className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-3 py-4">
        {NAVIGATION.map(({ section, items }) => (
          <div key={section}>
            <p className="px-2 pb-1.5 text-2xs font-bold tracking-[0.14em] uppercase text-ink-muted">
              {section}
            </p>
            <ul className="space-y-0.5">
              {items.map((item) => {
                const Icon = item.icon
                const badge = item.to === '/events' ? eventsCount : item.badge
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `group w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[12px] leading-5  transition-colors duration-100 ${isActive
                          ? 'bg-primary text-white font-semibold'
                          : 'text-text-secondary hover:bg-primary hover:text-white font-medium'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <Icon
                            aria-hidden="true"
                            strokeWidth={1.75}
                            className={`h-[15px] w-[15px] shrink-0 ${isActive ? 'text-white' : 'text-ink-muted group-hover:text-white'}`}
                          />
                          <span className="flex-1 text-left">{item.label}</span>
                          {badge != null && (
                            <span
                              className={`flex h-[18px] w-[18px] items-center justify-center rounded-full text-[9px] font-bold transition-colors ${isActive
                                  ? 'text-primary bg-white'
                                  : 'text-white bg-primary group-hover:bg-white group-hover:text-primary'
                                }`}
                            >
                              {badge}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="flex shrink-0 items-center gap-2.5 border-t border-border-dark/9 px-4 py-3.5">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-sidebar-active text-sidebar-fg text-xs-plus font-bold shrink-0">
          {user.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-semibold leading-tight truncate text-text-dark">
            {user.name}
          </p>
          <p className="text-2xs uppercase tracking-wide font-semibold text-primary">
            {user.role}
          </p>
        </div>
        {/* <button
          type="button"
          onClick={onLogout}
          aria-label="Sign out"
          title="Sign out"
          className="p-1.5 rounded-md text-sidebar-muted hover:text-sidebar-fg hover:bg-sidebar-active transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button> */}
        <ChevronDown className="w-4 h-4 text-ink-muted" />
      </div>
    </aside>
  )
}
